import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import { isOpenEntry } from '@/lib/registerRiskScore';
import { getSeverityBadgeColor } from './riskUtils';
import { severityOf, today, topEntries, registerSummary, type RegisterRow } from './registerRows';

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '');

/** The strongest open entries as findings, for the Overview. */
export function RegisterFindings({ rows, limit }: { rows: RegisterRow[]; limit: number }) {
  const { t, tx } = useLocalization();
  const day = today();
  return (
    <>
      {topEntries(rows, limit).map((r, i) => (
        <div key={`${r.ref ?? r.title}-${i}`} className="flex items-center justify-between gap-3 text-sm">
          <div className="flex min-w-0 items-center gap-2">
            <Badge className={cn('text-xs capitalize', getSeverityBadgeColor(severityOf(r)))}>{tx(severityOf(r))}</Badge>
            <span className="truncate">{tx(r.title)}</span>
          </div>
          <span className={cn('shrink-0 text-xs', r.review_on && r.review_on < day ? 'text-amber-500' : 'text-muted-foreground')}>
            {r.review_on && r.review_on < day ? `${t('Review overdue')} ${fmt(r.review_on)}` : r.owner ? tx(r.owner) : ''}
          </span>
        </div>
      ))}
    </>
  );
}

/** Why the register scores what it does: the reasons, then the entries behind them. */
export function RegisterWhy({ rows }: { rows: RegisterRow[] }) {
  const { t, tx } = useLocalization();
  const d = registerSummary(rows);
  if (d.open === 0) return null;
  const day = today();
  const reasons = [
    d.critical > 0 && `${d.critical} ${t('critical')}`,
    d.high > 0 && `${d.high} ${t('high')}`,
    d.escalated > 0 && `${d.escalated} ${t('escalated')}`,
    d.overdueReview > 0 && `${d.overdueReview} ${t('past their review date')}`,
  ].filter(Boolean).join(' · ');
  return (
    <section className="rounded-lg border bg-card p-4" aria-label={t('From the project documents')}>
      <p className="text-sm font-medium">{t('From the project documents')}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {t('Register score')} <span className="font-semibold text-foreground">{d.score}</span>: {d.open} {t('open risks and issues')}{reasons ? ` — ${reasons}` : ''}.
        {' '}{t('Each open item adds weight by rating; escalated and overdue items add more; accepted risks add less.')}
      </p>
      <ul className="mt-3 space-y-2">
        {topEntries(rows, 8).map((r, i) => (
          <li key={`${r.ref ?? r.title}-${i}`} className="rounded-md border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={cn('text-xs capitalize', getSeverityBadgeColor(severityOf(r)))}>{tx(severityOf(r))}</Badge>
              <span className="text-sm font-medium">{tx(r.title)}</span>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {[r.ref, r.status && tx(r.status), r.owner && `${t('Owner')}: ${r.owner}`, r.review_on && `${r.review_on < day ? t('Review overdue') : t('Review')} ${fmt(r.review_on)}`].filter(Boolean).join(' · ')}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** What the documents already say to do about the open entries. */
export function RegisterActions({ rows }: { rows: RegisterRow[] }) {
  const { t, tx } = useLocalization();
  const planned = topEntries(rows.filter((r) => isOpenEntry(r.status)), 12).filter((r) => r.mitigation);
  if (planned.length === 0) return null;
  const day = today();
  return (
    <section className="rounded-lg border bg-card p-4" aria-label={t('Planned in the project documents')}>
      <p className="text-sm font-medium">{t('Planned in the project documents')}</p>
      <ul className="mt-3 space-y-2">
        {planned.map((r, i) => (
          <li key={`${r.ref ?? r.title}-${i}`} className="rounded-md border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={cn('text-xs capitalize', getSeverityBadgeColor(severityOf(r)))}>{tx(severityOf(r))}</Badge>
              <span className="text-sm font-medium">{tx(r.title)}</span>
              {r.response && <Badge variant="outline" className="text-xs">{tx(r.response)}</Badge>}
            </div>
            <p className="mt-1.5 text-sm text-muted-foreground">{tx(r.mitigation ?? '')}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {[r.owner && `${t('Owner')}: ${r.owner}`, r.review_on && `${r.review_on < day ? t('Review overdue') : t('Review')} ${fmt(r.review_on)}`].filter(Boolean).join(' · ')}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}
